import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {useWalletTransferCompletedStepOnSuccess} from '#/components/wallet-transfer/use-wallet-transfer-completed-step-on-success';
import {useWalletTransferTokenInputHandlers} from '#/components/wallet-transfer/use-wallet-transfer-token-input-handlers';
import {WalletTransferContext} from '#/components/wallet-transfer/wallet-transfer-context';
import {
  ActionNextStepButtonContainer,
  SkipNextButton,
  StepHeader,
  Title,
  TokenTransferSingleStepButton,
  TokenTransferStatusContext,
  TokenTransferStatusProvider,
  TokenTransferStatusTitle,
} from '#/components/wallet-transfer/wallet-transfer-modal';
import {WalletTransferTab} from '#/components/wallet-transfer/wallet-transfer-tab';
import {WalletTransferTokenInputGroup} from '#/components/wallet-transfer/wallet-transfer-token-input-group';
import {useWalletProvider} from '#/contexts/wallet-provider';
import {useNativeTokenBalance} from '#/hooks/query/use-native-token-balance';
import {useStableTokenBalance} from '#/hooks/query/use-stable-token-balance';
import useERC20Contract from '#/hooks/use-erc20-contract';
import {getIsAddressValid} from '#/utils/account-utils';
import {stringToTokenAmount, tokenAmountToInputString} from '#/utils/token-string-utils';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {useMutation} from '@tanstack/react-query';
import {FunctionComponent, useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {waitForTransactionReceipt} from 'wagmi/actions';

export const WalletTransferStepStable: FunctionComponent = () => {
  const {address} = useContext(WalletTransferContext);
  const {
    accountResult: {address: account},
    refetchWalletBalance,
  } = useWalletProvider();

  const {t} = useTranslation('translation');

  const {refetch: refetchNativeTokenBalance} = useNativeTokenBalance();
  const {
    data: stableTokenBalance,
    isLoading: stableTokenBalanceIsLoading,
    refetch: refetchStableTokenBalance,
  } = useStableTokenBalance();
  const erc20Contract = useERC20Contract();

  const getInitialInputValue = () => tokenAmountToInputString(Number(stableTokenBalance));
  const {inputValue, onInputChange, onMaxButtonClick, inputAboveBalance} = useWalletTransferTokenInputHandlers(
    stableTokenBalance,
    getInitialInputValue,
  );

  const transferPossible = stableTokenBalance > 0n && !stableTokenBalanceIsLoading;

  const transferEnabled =
    !!account && getIsAddressValid(address) && !!erc20Contract && stableTokenBalance !== -1n && !!account;
  const transferMutation = useMutation({
    mutationKey: ['transfer-stable'],
    mutationFn: async () => {
      if (!transferEnabled) {
        throw new Error('Transfer Stable is not enabled');
      }

      const value = Math.round(stringToTokenAmount(inputValue));
      const hash = await erc20Contract.transfer(address, value);

      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      await refetchNativeTokenBalance();
      await refetchWalletBalance();

      return transactionReceipt;
    },
  });

  useWalletTransferCompletedStepOnSuccess(
    transferMutation.status === 'success' || !transferPossible,
    WalletTransferTab.STABLE,
  );
  useEffect(() => {
    return () => {
      if (transferMutation.status === 'success') {
        refetchStableTokenBalance();
      }
    };
  }, [transferMutation.status, refetchStableTokenBalance]);

  return (
    <>
      <StepHeader>{t('pages.walletTransfer.sections.stableToken.header')}</StepHeader>

      {transferPossible ? (
        <>
          <TokenTransferStatusProvider
            queryEnabled={transferEnabled}
            queryStatus={transferMutation.status}
            queryError={transferMutation.error as Error | null}
            queryStart={() => {
              transferMutation.mutate();
            }}
          >
            <div className='space-y-2'>
              <TokenTransferStatusTitle>
                {t('components.walletTransferTokenInputGroup.stable')}
              </TokenTransferStatusTitle>
              <Title>{t('pages.walletTransfer.sections.stableToken.explanation')}</Title>

              <WalletTransferTokenInputGroup
                value={inputValue}
                isAboveBalance={inputAboveBalance}
                onChange={onInputChange}
                onMaxAmountButtonClick={onMaxButtonClick}
                getInitialValue={getInitialInputValue}
              />

              <TokenTransferStatusContext.Consumer>
                {({status, error}) => (
                  <>
                    <ActionNextStepButtonContainer>
                      <TokenTransferSingleStepButton />
                      <SkipNextButton active={status === 'success'} />
                    </ActionNextStepButtonContainer>
                    {!!error && <NoticeMessageErrorDetails error={error as Error} />}
                  </>
                )}
              </TokenTransferStatusContext.Consumer>
            </div>
          </TokenTransferStatusProvider>
        </>
      ) : (
        <>
          <div className='grow flex-center text-base font-bold text-gray-400'>
            {t('pages.walletTransfer.sections.stableToken.noUsdcToTransfer')}
          </div>
          <ActionNextStepButtonContainer>
            <SkipNextButton active={true} />
          </ActionNextStepButtonContainer>
        </>
      )}
    </>
  );
};
